import React, { useState, useEffect } from 'react'
import { BsBoxArrowInUpRight } from 'react-icons/bs'
import { BiCalendarCheck } from 'react-icons/bi'
import { useMutation, useQueryClient } from 'react-query'
import { dateFormaterWithTime, dateFormatter, dateFormatterGmailTime } from '../../../../../helpers/formatters'
import useNav from '../../../../../hooks/useNav';
import Toggle from '../../../../../components/toggle'
import { API_AUTH_TYPES, API_ROUTES, ApiRequest } from '../../../../../api'
import MeetingDetails from '../../../../../components/modals/MeetingDetails'
import Highlighter from 'react-highlight-words'

// Define the mapping between meeting types and color classes
const meetingTypeColors = {
    'Prospect': 'bg-green-100 text-green-700 ring-green-400',
    'Onboarding': 'bg-yellow-100 text-yellow-700 ring-yellow-400',
    'Check-In': 'bg-blue-100 text-blue-700 ring-blue-400',
    'Trading': 'bg-purple-100 text-purple-700 ring-purple-400',
    'Planning': 'bg-red-100 text-red-700 ring-red-400',
};

const meetingsApi = new ApiRequest(API_ROUTES.CLIENT_MEETINGS, API_AUTH_TYPES.ADVISOR);

/**
 * Component to render a card for an upcoming meeting, showing details such as household name,
 * meeting type, start date/time, and optionally the first question's answer if available.
 *
 * @param {Object} props Component props.
 * @param {Object} props.meeting The meeting object containing details to display.
 * @param {string} props.query Search query for highlighting.
 * @returns {JSX.Element} The card element for a single meeting.
 */
const MeetingsCard = ({ meeting, query }) => {
    // Determine the CSS classes for the meeting type badge based on the meeting's type
    // Fallback to a default style if the meeting type is not recognized
    const colorClasses = meetingTypeColors[meeting.meeting_type] || 'bg-gray-100 text-gray-700 ring-gray-600';
    const [showMeetingDetails, setShowMeetingDetails] = useState(false);
    const [prepCompleted, setPrepCompleted] = useState(meeting.prep_completed || false);
    const nav = useNav();
    const queryClient = useQueryClient();

    useEffect(() => {
        setPrepCompleted(meeting.prep_completed || false);
    }, [meeting.prep_completed]);

    const updatePrepStatus = useMutation(
        (isCompleted) => {
            return meetingsApi.patch({
                endpoint: "update_prep_completed",
                params: [meeting.meeting_id],
                body: { prep_completed: isCompleted },
            });
        },
        {
            onSuccess: () => {
                setTimeout(() => {
                    queryClient.invalidateQueries("upcomingMeetings");
                    queryClient.invalidateQueries(`meeting-${meeting.meeting_id}`);
                }, 1000);
            },
        }
    );

    const handlePrepToggle = (isCompleted) => {
        setPrepCompleted(isCompleted);
        updatePrepStatus.mutate(isCompleted);
    };

    const handleCardClick = () => {
        setShowMeetingDetails(true);
    };

    // Prepare search words for highlighting
    const searchWords = query && query.trim() !== '' ?
        (Array.isArray(query) ? query : [query]) :
        [];

    return (
        <>
            {showMeetingDetails && <MeetingDetails meetingId={meeting.meeting_id} onClose={() => setShowMeetingDetails(false)} />}

            <div
                className="bg-white rounded-lg border border-gray-200 p-3.5 hover:shadow-md transition-shadow group cursor-pointer"
                onClick={handleCardClick}
            >
                <div className="flex flex-col">
                    {/* Top section: Meeting info */}
                    <div className="mb-3.5">
                        <div className="flex justify-between">
                            {/* Left column: Name and meeting details */}
                            <div className="flex-1">


                                <div className="flex flex-wrap items-center gap-2 mt-2">
                                    <div className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${colorClasses}`}>
                                        {meeting.meeting_type}
                                    </div>
                                    <span className="text-base text-gray-800">{dateFormatter.format(new Date(meeting.start_time))}</span>
                                </div>
                            </div>

                            {/* Right column: Actions */}
                            <div className="flex flex-col items-end justify-between space-y-3 ml-3">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowMeetingDetails(true);
                                    }}
                                    className="text-xs font-medium text-blue-600 hover:text-blue-800 text-right flex items-center opacity-80 hover:opacity-100"
                                >
                                    <BiCalendarCheck className="mr-1 h-3.5 w-3.5" />
                                    View Details
                                </button>

                                <div className="flex items-center gap-1.5">
                                    <span className="text-xs text-gray-500 mb-0.5">Prep Completed</span>
                                    <div className="toggle-container" onClick={e => e.stopPropagation()}>
                                        <Toggle
                                            enabled={prepCompleted}
                                            setEnabled={handlePrepToggle}
                                            color={prepCompleted ? "green" : "gray"}
                                            size="sm"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom section: Meeting reason */}
                    <div className="border-t border-gray-100 pt-3">
                        <div className="text-xs uppercase text-gray-500 font-medium mb-1.5">Meeting Reason</div>
                        <div className="text-sm text-gray-800">
                            {meeting.questions_and_answers && meeting.questions_and_answers[0]?.answer ? (
                                searchWords.length > 0 ? (
                                    <Highlighter
                                        highlightClassName="bg-yellow-200 text-yellow-900"
                                        searchWords={searchWords}
                                        autoEscape={true}
                                        textToHighlight={meeting.questions_and_answers[0].answer}
                                    />
                                ) : (
                                    meeting.questions_and_answers[0].answer
                                )
                            ) : (
                                <span className="text-gray-400 italic">No reason provided</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default MeetingsCard 